import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import axios from '../config/axios'
import UserContext, { Roles } from './UserContext'

interface UserPreferencesContextType {
  preferences: Record<string, any>
  getPreference: <T>(key: string, defaultValue?: T) => T
  getEffectivePreference: <T>(key: string, defaultValue?: T, customUseMyPrefsKey?: string) => T
  setPreference: <T>(key: string, value: T) => Promise<void>
  isLoading: boolean
  reloadPreferences: () => Promise<void>
  isTenantAdmin: boolean
}

const UserPreferencesContext = createContext<UserPreferencesContextType | undefined>(undefined)

export const UserPreferencesProvider: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const [preferences, setPreferences] = useState<Record<string, any>>({})
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const { user } = useContext(UserContext)

  const isTenantAdmin = (user?.role as any) === 'TENANT_ADMIN' || (user?.role as any) === Roles.tenantAdmin

  const reloadPreferences = useCallback(async () => {
    const token = localStorage.getItem('token')
    if (!token) {
      setIsLoading(false)
      return
    }

    try {
      const response = await axios.get('/pipelines/user-preference')
      if (response.data) {
        setPreferences(response.data)
      }
    } catch (error) {
      console.error('Erro ao carregar preferências do usuário:', error)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    reloadPreferences()
  }, [reloadPreferences])

  const getPreference = useCallback(<T,>(key: string, defaultValue?: T): T => {
    const val = preferences[key]
    if (val === undefined) {
      return defaultValue as T
    }
    return val as T
  }, [preferences])

  const getEffectivePreference = useCallback(<T,>(key: string, defaultValue?: T, customUseMyPrefsKey?: string): T => {
    if (isTenantAdmin) {
      return getPreference<T>(key, defaultValue)
    }

    const useMyPrefKey = customUseMyPrefsKey || `useMyPreferences_${key}`
    const useMyPref = getPreference<boolean>(useMyPrefKey, false)
    if (useMyPref) {
      return getPreference<T>(key, defaultValue)
    }

    // Default to admin's preference, fallback to user's preference, fallback to default value
    return getPreference<T>(`admin_${key}`, getPreference<T>(key, defaultValue))
  }, [getPreference, isTenantAdmin])

  const setPreference = useCallback(async <T,>(key: string, value: T) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: value,
    }))

    try {
      await axios.post('/pipelines/user-preference', { key, value })
    } catch (error) {
      console.error(`Erro ao salvar preferência ${key} no banco:`, error)
    }
  }, [])

  return (
    <UserPreferencesContext.Provider value={{ preferences, getPreference, getEffectivePreference, setPreference, isLoading, reloadPreferences, isTenantAdmin }}>
      {children}
    </UserPreferencesContext.Provider>
  )
}

export const useUserPreferences = () => {
  const context = useContext(UserPreferencesContext)
  if (context === undefined) {
    throw new Error('useUserPreferences must be used within a UserPreferencesProvider')
  }
  return context
}
